import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, InputAdornment, IconButton, Stack, Alert, Paper, Typography,
} from '@mui/material';
import { Share as ShareIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

const SPORTS_WORDS = ['Sprint', 'Kraul', 'Deckel', 'Taucher', 'Flosse', 'Turbo', 'Anpfiff', 'Dribbel', 'Abwurf', 'Heber', 'Koralle', 'Strömung', 'Pokal', 'Pass', 'Torpedo', 'Welle', 'Blitz', 'Raserei', 'Striker', 'Wurf', 'Schuss', 'Pfeil', 'Ruder', 'Flut', 'Gischt', 'Korbjagd', 'Abtaucher', 'Bodenkampf', 'Pressing', 'Untergriff', 'Konterzug', 'Tauchgang', 'Wasserlage', 'Grundpass', 'Doppeldeckel', 'Drehtor'];
const FUNNY_WORDS = ['Rakete', 'Keks', 'Hai', 'Blitz', 'Ninja', 'Wiesel', 'Komet', 'Pirat', 'Wirbel', 'Flummi', 'Zebra', 'Pudding', 'Bumerang', 'Gurke', 'Marshmallow', 'Pixel', 'Quark', 'Schnitzel', 'Tornado', 'Wackelpudding', 'Zimtschnecke', 'Biene', 'Delfin', 'Erdbeere', 'Frosch', 'Krake', 'Murane', 'Boje', 'Seehund', 'Neoprennase', 'Luftblase', 'Seegurke', 'Tiefseetiger', 'Flossenfuchs'];
const FALLBACK_FIRST_NAME = 'Spieler';

const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];

const sanitizeNameFragment = (value) => {
  const cleaned = (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z]/g, '');

  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

const buildReadablePassword = (firstName, playerName) => {
  const namePart = sanitizeNameFragment(firstName) || sanitizeNameFragment(playerName) || FALLBACK_FIRST_NAME;
  let sportsWord = pickRandom(SPORTS_WORDS);
  let funnyWord = pickRandom(FUNNY_WORDS);

  if (sportsWord === funnyWord) {
    funnyWord = FUNNY_WORDS[(FUNNY_WORDS.indexOf(funnyWord) + 1) % FUNNY_WORDS.length];
  }

  const suffix = String(Math.floor(Math.random() * 90) + 10);
  const generated = `${namePart}${sportsWord}${funnyWord}${suffix}`;

  if (generated.length >= 8) return generated;
  return `${namePart}${sportsWord}${suffix}7`;
};

export default function SetPasswordDialog({ open, playerName, firstName, loginEmail, requireEmail, onConfirm, onCancel }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [exportDone, setExportDone] = useState(false);

  const reset = () => { setPassword(''); setConfirm(''); setEmailInput(''); setError(''); setShowPw(false); setSavedCredentials(null); setExportDone(false); };

  const generatePassword = () => {
    const generated = buildReadablePassword(firstName, playerName);
    setPassword(generated); setConfirm(generated); setShowPw(true); setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (requireEmail) {
      if (!emailInput.trim()) { setError('Bitte eine E-Mail-Adresse eingeben.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim())) { setError('Ungültige E-Mail-Adresse.'); return; }
    }
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
    if (password !== confirm) { setError('Passwörter stimmen nicht überein.'); return; }
    setLoading(true);
    const err = await onConfirm(password, requireEmail ? emailInput.trim() : undefined);
    setLoading(false);
    if (err) { setError(err); return; }
    const effectiveEmail = requireEmail ? emailInput.trim() : loginEmail;
    setSavedCredentials({ email: effectiveEmail, password });
  };

  const handleExport = async () => {
    if (!savedCredentials) return;
    const link = window.location.origin;
    const text = `Zugangsdaten UWR Manager\n\nLink: ${link}\nE-Mail: ${savedCredentials.email}\nPasswort: ${savedCredentials.password}\n\nBeim ersten Login muss das Passwort geändert werden.`;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS && typeof navigator?.share === 'function') {
      try { await navigator.share({ text }); setExportDone(true); return; } catch {}
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setExportDone(true); return;
      }
    } catch {}
    if (typeof navigator?.share === 'function') {
      try { await navigator.share({ text }); setExportDone(true); return; } catch {}
    }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0';
    ta.style.width = '1px'; ta.style.height = '1px'; ta.style.opacity = '0.01'; ta.style.pointerEvents = 'none';
    document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    setExportDone(true);
  };

  const handleClose = () => { reset(); onCancel(); };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Passwort setzen – {playerName}</DialogTitle>
      {savedCredentials ? (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Alert severity="success">Passwort erfolgreich gesetzt.</Alert>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="body2" gutterBottom><strong>Link:</strong> {window.location.origin}</Typography>
                <Typography variant="body2" gutterBottom><strong>E-Mail:</strong> {savedCredentials.email}</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}><strong>Passwort:</strong> {savedCredentials.password}</Typography>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="inherit">Schließen</Button>
            <Button variant="outlined" startIcon={<ShareIcon />} onClick={handleExport}
              color={exportDone ? 'success' : 'primary'}>
              {exportDone ? 'Gesendet ✓' : 'Zugangsdaten senden'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {requireEmail && (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  Kein Login-E-Mail hinterlegt. Bitte jetzt eine echte E-Mail-Adresse festlegen.
                </Alert>
              )}
              {requireEmail && (
                <TextField label="E-Mail (Login)" type="email" fullWidth required autoFocus
                  value={emailInput} onChange={e => setEmailInput(e.target.value)}
                  helperText="Wird als Login-E-Mail gespeichert" />
              )}
              <Alert severity="info" sx={{ mb: 0 }}>
                Der Spieler muss das Passwort beim ersten Login ändern.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Der Generator erstellt lesbare Startpasswörter mit Vornamen und sportlichen Spaßwörtern.
              </Typography>
              <Button variant="outlined" size="small" onClick={generatePassword}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                Passwort generieren
              </Button>
              <TextField
                label="Neues Passwort" fullWidth required autoComplete="new-password"
                type={showPw ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
                helperText="Mindestens 8 Zeichen"
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPw(v => !v)}>
                      {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ) }} />
              <TextField
                label="Passwort bestätigen" fullWidth required autoComplete="new-password"
                type={showPw ? 'text' : 'password'}
                value={confirm} onChange={e => setConfirm(e.target.value)} />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="inherit">Abbrechen</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Speichern…' : 'Passwort setzen'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
